import { Form, Formik } from "formik";
import React, { FC, ReactElement, useState } from "react";
import { useGlobalState } from "../../../Providers";
import { LICENSE_VINGCARD } from "../../../constants/license-constants";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useLocale } from "../../../contexts/localeContext";
import { EnrolmentDevice } from "../../../models/EnrolmentDevices";
import { EnrolmentTask } from "../../../models/EnrolmentTask";
import { Tag } from "../../../models/Tag";
import WizardSideBarStep from "../../../models/WizardSideBarStep";
import { IShowEnrollValidOptions } from "../../../services/wsPluginService/plugins/base/pluginModels";
import { useDialog } from "../../dialog/DialogContext";
import Wizard from "../../wizard/wizard";
import SubTasksForm from "../sub-tasks-form";
import CardType from "./vingcard/card-type";
import DetailForm from "./vingcard/detail-form";
import Door from "./vingcard/door";
import Summary from "./vingcard/summary";

interface EncodeCardWizardProps {
    defaultContent: ReactElement[];
    defaultSteps: WizardSideBarStep[];
    onBack: (event: any) => void;
    onClose: () => void;
    cardInfo: Tag;
    onSave: (values) => void;
    availablePlugins: IShowEnrollValidOptions;
    availabilityResolved: boolean;
    availableSystemDeviceIds: number[];
}

const EncodeCardWizard: FC<EncodeCardWizardProps> = ({ defaultContent, defaultSteps, onBack, onClose, cardInfo, onSave, availablePlugins, availabilityResolved, availableSystemDeviceIds }) => {
    const { translate } = useLocale();
    const [step, setStep] = useState<number>(1);
    const dialog = useDialog();
    const [vingCardStepDescription, setVingCardStepDescription] = useState<{ cardType: string, door: string }>({ cardType: '', door: '' });
    const { buildInfo } = useGlobalState() ?? {};
    const enrolmentDevice = new EnrolmentDevice(translate);
    const [vingCardType, setVingCardType] = useState<"staff" | "guest">('guest');
    const enrolmentTask = new EnrolmentTask(translate);
    const [taskDescription, setTaskDescription] = useState('');

    const steps: WizardSideBarStep[] = [
        ...defaultSteps,
        {
            title: translate.byKey(TranslationKeys.taskProcedure),
            description: taskDescription,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.cardType),
            description: vingCardStepDescription.cardType,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.doors),
            description: vingCardStepDescription.door,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.enterDetails),
            description: '',
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: '',
            substeps: []
        }
    ];

    const onNext = (event) => {
        if (event.task && event.task === enrolmentDevice.VINGCARD.value) {
            // Requires a license but feature is not licensed
            const isLicensed = buildInfo?.licenceIds?.includes(LICENSE_VINGCARD) ?? false;
            if (!isLicensed) {
                dialog.pushError({ title: translate.byKey(TranslationKeys.unlicensed), message: translate.byKey(TranslationKeys.unlicensedFeature) });
                event.setTaskSelected(false);
                return;
            }
            setTaskDescription(event.description);
        }

        setStep(step + 1);
    }

    const onLocalBack = (event) => {
        if (step === 1) {
            onBack(event);
            return;
        }
        else if (step === 2) {
            setTaskDescription("");
        }

        setStep(step - 1);
    }

    const content: ReactElement[] = [
        ...defaultContent,
        <SubTasksForm
            onNext={(event) => onNext(event)}
            pluginsAvailable={availablePlugins}
            onBack={(event) => onLocalBack(event)}
            enrollMethod={enrolmentTask.ENCODE_CARD.value}
            availabilityResolved={availabilityResolved}
            availableSystemDeviceIds={availableSystemDeviceIds} />,
        <CardType
            onNext={(event) => onNext(event)}
            onBack={(event) => onLocalBack(event)}
            setVingCardStepDescription={setVingCardStepDescription}
            setVingCardType={setVingCardType} />,
        <Door
            onNext={(event) => onNext(event)}
            onBack={(event) => onLocalBack(event)}
            setVingCardStepDescription={setVingCardStepDescription} />,
        <DetailForm
            onNext={(event) => onNext(event)}
            onBack={(event) => onLocalBack(event)}
            vingCardType={vingCardType} />,
        <Summary
            onBack={(event) => onLocalBack(event)}
            taskDescription={steps[1].description}
            taskProcedure={steps[0].description}
            vingCardDescription={vingCardStepDescription} />
    ]


    return <Formik
        enableReinitialize={true}
        initialValues={cardInfo}
        onSubmit={onSave}>
        <Form>
            <Wizard name={translate.byKey(TranslationKeys.enrollCredential)}
                step={step}
                onClose={() => onClose()}
                sidebarSteps={steps}>
                {content}
            </Wizard>
        </Form>
    </Formik>
}

export default EncodeCardWizard;